import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { getTokenExpiresAt, getTokens } from '../common/manage_tokens';
import { expireSession } from '../common/session';

/** setTimeout's ceiling. A longer delay overflows and fires immediately. */
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * Gate for every dashboard route.
 *
 * Decided during render rather than in an effect, so no page mounts — and no
 * page fires its queries with a missing or dead token — before the redirect.
 *
 * This only checks that a token exists and has not passed its own `exp`. The
 * ADMIN role is checked at login, and anything else the API refuses (a
 * revoked token, a role change, a ban) comes back through the error link and
 * `SessionExpiryHandler`.
 */
export const RequireAuth = ({ children }: { children: ReactNode }) => {
  // Subscribing to the location re-runs this check on every navigation, not
  // only when the dashboard first mounts.
  useLocation();

  const token = getTokens();
  const expiresAt = token ? getTokenExpiresAt(token) : null;
  const isExpired = expiresAt !== null && expiresAt <= Date.now();

  // Signs out at the moment the token lapses, so an idle tab does not sit on
  // a screen whose next save is certain to fail.
  useEffect(() => {
    if (expiresAt === null) return;

    const delay = expiresAt - Date.now();

    // The API's default lifetime is 30 days, past setTimeout's ~24.8-day
    // limit. Such a token is still caught by the render check on a later
    // navigation, and by the error link on its next request.
    if (delay > MAX_TIMEOUT_MS) return;

    const timer = setTimeout(expireSession, Math.max(delay, 0));

    return () => clearTimeout(timer);
  }, [expiresAt]);

  if (!token) return <Navigate to="/account/login" replace={true} />;

  // Past `exp` already: render nothing, and let the effect above clear it
  // straight away, so the toast and redirect match any other expiry.
  if (isExpired) return null;

  return <>{children}</>;
};
